import React from 'react';
import { Link } from 'react-router-dom';
import { Menu as AntdMenu, Tooltip } from 'antd';
import { ExternalAuthContext } from 'contexts';
import { featureEnabled } from 'utils';
import Icon from '../../Icon';
import { StyledMenu, StyledShowingIcon } from '../StyledComponents';
import { StyledMenuProps } from './types';

const DashboardMenu: React.FC<StyledMenuProps> = ({
  selectedKey,
  openKeys,
  setOpenKeys,
  listings,
  disableToolTip,
  getIconColor,
}) => {
  const {
    auth: {
      dashboardId,
      externalDashboard,
      buyerShowingsCount,
      hasFeature,
      listing,
      hasBookedShowing,
      user,
    },
  } = React.useContext(ExternalAuthContext);

  const pageName = featureEnabled('propertyNotificationsRevampFeature', 1)
    ? 'Property Notifications'
    : 'Property Updates';

  const isCustomContact = React.useMemo(
    () =>
      'contactType' in user &&
      (user.contactType === 'customagent' || user.contactType === 'nonagent'),
    [user],
  );

  const isListingUnclaimed = React.useMemo(
    () => Boolean(listing?.isUnclaimed),
    [listing],
  );

  const tooltips = React.useMemo<{
    showing: string | null;
    offer: string | null;
  }>(() => {
    if (!disableToolTip) {
      if (isListingUnclaimed) {
        return {
          showing:
            'You cannot Book a Showing as this listing is not represented by BrokerBay.',
          offer:
            'You cannot Register an Offer as this listing is not represented by BrokerBay.',
        };
      }
      return { showing: 'Showings', offer: 'Register Offer' };
    }
    return { showing: null, offer: null };
  }, [disableToolTip, isListingUnclaimed]);

  const coopDashboardItems = (
    <>
      <AntdMenu.Item
        icon={<Icon type="home" color={getIconColor('0')} />}
        key="View Listing"
        title={disableToolTip ? null : 'View Listing'}
      >
        <Link to={`/dash/${dashboardId}/listing`}>
          <span>View Listing</span>
        </Link>
      </AntdMenu.Item>
      <AntdMenu.Item
        icon={<Icon type="appointment" color={getIconColor('1')} />}
        key={tooltips.showing}
        title={tooltips.showing}
        disabled={isListingUnclaimed}
      >
        <Link to={`/dash/${dashboardId}/appointments`}>
          <span>Showings</span>
        </Link>
      </AntdMenu.Item>
      {hasFeature('OFFERS') && (
        <AntdMenu.Item
          icon={<Icon type="fileText" color={getIconColor('2')} />}
          key={tooltips.offer}
          title={tooltips.offer}
          disabled={isListingUnclaimed}
        >
          <Link to={`/dash/${dashboardId}/offers`}>
            <span>Register Offer</span>
          </Link>
        </AntdMenu.Item>
      )}
      <AntdMenu.Item
        icon={<Icon type="bell" color={getIconColor('3')} />}
        key={pageName}
        title={disableToolTip ? null : pageName}
        disabled={
          !hasBookedShowing &&
          !featureEnabled('propertyNotificationsRevampFeature', 1)
        }
      >
        <Tooltip
          title={
            !hasBookedShowing &&
            !featureEnabled('propertyNotificationsRevampFeature', 1)
              ? 'Book a showing on the property to receive updates including registered offers, property announcements, and feedback reminders'
              : null
          }
          placement="right"
        >
          <div>
            <Link to={`/dash/${dashboardId}/updates`}>
              <span>{pageName}</span>
            </Link>
          </div>
        </Tooltip>
      </AntdMenu.Item>
    </>
  );

  const externalDashboardItems = (
    <>
      {listings && listings.length > 0 && (
        <AntdMenu.SubMenu
          key="myListings"
          icon={<Icon type="home" color={getIconColor('myListings')} />}
          title="My Listings"
        >
          {listings.map(({ line1, line2, id: listingId }) => (
            <AntdMenu.Item key={`myListings-${listingId}`}>
              <Link to={`/dash/${dashboardId}/listing/${listingId}`}>
                {`${line1}${line2 ? ` ${line2}` : ''}`}
              </Link>
            </AntdMenu.Item>
          ))}
        </AntdMenu.SubMenu>
      )}
      {buyerShowingsCount ||
      isCustomContact ||
      listings?.filter(({ appointmentCount }) => appointmentCount).length ? (
        <AntdMenu.SubMenu
          key="showings"
          icon={
            <StyledShowingIcon
              type="antdCalendarFilled"
              color={getIconColor('showings')}
            />
          }
          title="Showings"
        >
          {(buyerShowingsCount || isCustomContact) && (
            <AntdMenu.Item key="showings-buyer">
              <Link to={`/dash/${dashboardId}/buyer_contact_appointments`}>
                My Showings
              </Link>
            </AntdMenu.Item>
          )}
          {listings
            ?.filter(({ appointmentCount }) => appointmentCount)
            .map(({ line1, line2, id: listingId }) => (
              <AntdMenu.Item key={`showings-${listingId}`}>
                <Link
                  to={`/dash/${dashboardId}/appointments/listing/${listingId}`}
                >
                  {`${line1}${line2 ? ` ${line2}` : ''}`}
                </Link>
              </AntdMenu.Item>
            ))}
        </AntdMenu.SubMenu>
      ) : null}
      {!isCustomContact && (
        <AntdMenu.Item
          icon={<Icon type="bell" color={getIconColor('4')} />}
          key="4"
          title={disableToolTip ? null : 'Notifications'}
        >
          <Link to={`/dash/${dashboardId}/notifications`}>
            <span>My Notifications</span>
          </Link>
        </AntdMenu.Item>
      )}
      {!isCustomContact && (
        <AntdMenu.Item
          icon={<Icon type="people" color={getIconColor('5')} />}
          key="5"
          title={disableToolTip ? null : 'My Agent'}
        >
          <Link to={`/dash/${dashboardId}/my_agent`}>
            <span>My Agent</span>
          </Link>
        </AntdMenu.Item>
      )}
    </>
  );

  return (
    <StyledMenu
      mode="inline"
      selectedKeys={[selectedKey]}
      openKeys={openKeys}
      onOpenChange={keys => {
        setOpenKeys(keys);
      }}
      $hasSupraRebrandingFeature={featureEnabled('supraRebrandingFeature', 1)}
    >
      {externalDashboard ? externalDashboardItems : coopDashboardItems}
    </StyledMenu>
  );
};

export default DashboardMenu;
